import './AevocamActionBar.css'
import { useMemo } from 'react'
import { AevocamActionButton } from './AevocamActionButton'
import { AevocamActionSelect } from './AevocamActionSelect'
import { Icon } from './Icon'
import { playbackIndexBounds, type PlaybackRepeatMode, useAevocamPlayer } from './useAevocamPlayer'

const PLAYBACK_REPEAT_CYCLE: PlaybackRepeatMode[] = ['single', 'loop', 'oscillate']

export function AevocamActionBar() {
  const {
    currentIndex,
    isPlaying,
    playReverse,
    playbackRepeat,
    inFrame,
    outFrame,
    stayBetweenInOut,
    frameRate,
    frameRateOptions,
    timeSlot,
    markOutliers,
    loadingStage,
    totalPhotos,
    currentPhoto,
    zoomLevel,
    isFullScreen,
    actions,
  } = useAevocamPlayer()

  const hourOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [
      { value: '', label: '(all hours)' },
    ]
    for (let h = 0; h < 24; h++) {
      const value = `${String(h).padStart(2, '0')}0000`
      let label: string
      if (h === 0) label = 'Midnight'
      else if (h === 12) label = 'Noon'
      else {
        const pm = h >= 12
        const h12 = h > 12 ? h - 12 : h
        label = `${h12}${pm ? 'pm' : 'am'}`
      }
      opts.push({ value, label })
    }
    return opts
  }, [])

  const frameRateSelectOptions = useMemo(
    () =>
      frameRateOptions.map((fps) => ({
        value: String(fps),
        label: `${fps === 0.5 ? '1/2' : fps} fps`,
      })),
    [frameRateOptions],
  )

  const { playbackLo, playbackHi } = useMemo(() => {
    const { lo, hi } = playbackIndexBounds(stayBetweenInOut, inFrame, outFrame, totalPhotos)
    return { playbackLo: lo, playbackHi: hi }
  }, [stayBetweenInOut, inFrame, outFrame, totalPhotos])

  if (loadingStage < 2) return null

  return (
    <div className='AevocamActionBar'>
      <div className='Section'>

        <AevocamActionButton
          tooltip='Mark statistical outliers on the timeline'
          className={markOutliers ? 'There' : ''}
          onClick={() => actions.chooseMarkOutliers(!markOutliers)}
        >
          <Icon icon='exclamationTriangle' />
        </AevocamActionButton>

        <AevocamActionSelect
          tooltip='Narrow to time slot or view all times'
          value={timeSlot ?? ''}
          options={hourOptions}
          onChange={(value) => {
            actions.chooseTimeSlot(value === '' ? undefined : value)
          }}
        />

      </div>
      <div className='Section'>

        <AevocamActionButton
          tooltip='First frame'
          disabled={currentIndex === playbackLo}
          onClick={() => actions.goFirst(stayBetweenInOut)}
        >
          <Icon icon='stepForward' reverse />
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Previous frame'
          disabled={currentIndex === playbackLo}
          onClick={() => {
            actions.stop()
            actions.setDirection(true)
            actions.advance(-1, stayBetweenInOut)
          }}
        >
          <Icon icon='play' reverse className='Squished' />
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Next frame'
          disabled={currentIndex === playbackHi}
          onClick={() => {
            actions.stop()
            actions.setDirection(false)
            actions.advance(1, stayBetweenInOut)
          }}
        >
          <Icon icon='play' className='Squished' />
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Last frame'
          disabled={currentIndex === playbackHi}
          onClick={() => actions.goLast(stayBetweenInOut)}
        >
          <Icon icon='stepForward' />
        </AevocamActionButton>

      </div>
      <div className='Section'>

        <AevocamActionButton
          tooltip='Play reverse'
          disabled={currentIndex === playbackLo}
          onClick={() => actions.playPause(true)}
        >
          <Icon
            icon='play'
            reverse
            className={isPlaying && playReverse ? 'On' : ''}
          />
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Pause'
          onClick={isPlaying
            ? () => actions.stop()
            : () => actions.play()
          }
        >
          <Icon icon='pause' />
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Play forward'
          disabled={currentIndex === playbackHi}
          onClick={() => actions.playPause(false)}
        >
          <Icon
            icon='play'
            className={isPlaying && !playReverse ? 'On' : ''}
          />
        </AevocamActionButton>

      </div>
      <div className='Section'>

        <AevocamActionButton
          tooltip={
            inFrame >= 0 && currentIndex === inFrame
              ? 'Clear range start'
              : 'Set range start'
          }
          className={inFrame >= 0 && currentIndex === inFrame ? 'There' : ''}
          onClick={() => {
            if (currentIndex === inFrame) {
              actions.setInFrame(-1)
            } else {
              actions.setInFrame(currentIndex)
            }
          }}
        >
          [
        </AevocamActionButton>

        <AevocamActionButton
          tooltip={<>
            Repeat mode:
            <br/>{playbackRepeat === 'single'    ? '✓' : <>&nbsp;&nbsp;</>} Single
            <br/>{playbackRepeat === 'loop'      ? '✓' : <>&nbsp;&nbsp;</>} Loop
            <br/>{playbackRepeat === 'oscillate' ? '✓' : <>&nbsp;&nbsp;</>} Oscillate
          </>}
          onClick={() => {
            const i = PLAYBACK_REPEAT_CYCLE.indexOf(playbackRepeat)
            const next = (i < 0 ? 0 : i + 1) % PLAYBACK_REPEAT_CYCLE.length
            actions.setPlaybackRepeat(PLAYBACK_REPEAT_CYCLE[next]!)
          }}
        >
          {playbackRepeat === 'single' && (
            <Icon icon='longArrowRight' reverse={playReverse} />
          )}
          {playbackRepeat === 'loop' && <Icon icon='repeat' />}
          {playbackRepeat === 'oscillate' && <Icon icon='arrowsHorizontal' />}
        </AevocamActionButton>

        <AevocamActionButton
          tooltip={
            outFrame >= 0 && currentIndex === outFrame
              ? 'Clear range end'
              : 'Set range end'
          }
          className={outFrame >= 0 && currentIndex === outFrame ? 'There' : ''}
          onClick={() => {
            if (currentIndex === outFrame) {
              actions.setOutFrame(-1)
            } else {
              actions.setOutFrame(currentIndex)
            }
          }}
        >
          ]
        </AevocamActionButton>

      </div>
      <div className='Section'>

        <AevocamActionSelect
          tooltip='Playback speed'
          value={frameRate}
          options={frameRateSelectOptions}
          onChange={(value) => actions.chooseFrameRate(parseFloat(value))}
        />

      </div>
      <div className='Section'>

        <AevocamActionButton
          tooltip='Zoom out'
          onClick={() => actions.zoomOut()}
        >
          <Icon icon='magnifyingGlassMinus' />
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Click for 100%'
          onClick={() => actions.zoomActual()}
        >
          {Math.round(zoomLevel * 100)}%
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Zoom in'
          onClick={() => actions.zoomIn()}
        >
          <Icon icon='magnifyingGlassPlus' />
        </AevocamActionButton>
        <AevocamActionButton
          tooltip='Zoom to fit'
          onClick={() => actions.zoomFit()}
        >
          <Icon icon='fitScreen' />
        </AevocamActionButton>

      </div>
      <div className='Section'>

        <AevocamActionButton
          tooltip={isFullScreen ? 'Exit full screen' : 'Full screen'}
          disabled={!currentPhoto || loadingStage < 2}
          onClick={() => {
            void actions.fullScreen()
          }}
        >
          {isFullScreen
            ? <Icon icon='fullScreenExit' />
            : <Icon icon='fullScreen' />
          }
        </AevocamActionButton>
        {!isFullScreen && (
          <AevocamActionButton
            tooltip='Open this image in a new tab'
            disabled={!currentPhoto || loadingStage < 2}
            onClick={() => actions.openPhotoInNewTab()}
          >
            <Icon icon='image' />
          </AevocamActionButton>
        )}

      </div>
    </div>
  )
}
